import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Container,
  Typography,
  Button,
  IconButton,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  alpha,
} from '@mui/material';
import { Menu as MenuIcon, Close } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { gradients } from '../../theme/v2Theme';

interface NavBarProps {
  onOpenAuth?: () => void;
  isAuthenticated?: boolean;
}

export const NavBar: React.FC<NavBarProps> = ({ onOpenAuth, isAuthenticated = false }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const history = useHistory();

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Create Payment', path: '/pay' },
    { label: 'About', path: '/about' },
  ];

  const handleNavClick = (path: string) => {
    history.push(path);
    setMobileOpen(false);
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: alpha('#ffffff', 0.8),
          backdropFilter: 'blur(20px)',
          boxShadow: '0px 2px 24px rgba(99, 102, 241, 0.08)',
          color: 'text.primary',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            <Typography
              onClick={() => history.push('/')}
              sx={{
                fontWeight: 900,
                fontSize: '1.5rem',
                background: gradients.primary,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                cursor: 'pointer',
              }}
            >
              addrs
            </Typography>

            {/* Desktop Menu */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, ml: 4 }}>
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  onClick={() => handleNavClick(item.path)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.9375rem',
                    color: 'text.primary',
                    '&:hover': {
                      background: (theme) => alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>

            {/* Desktop Auth Buttons */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2 }}>
              {isAuthenticated ? (
                <Button
                  variant="contained"
                  onClick={() => history.push('/dashboard')}
                  sx={{
                    background: gradients.primary,
                    textTransform: 'none',
                    fontWeight: 700,
                    '&:hover': {
                      background: gradients.primary,
                      opacity: 0.9,
                    },
                  }}
                >
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button
                    onClick={onOpenAuth}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.9375rem',
                      color: 'text.primary',
                      '&:hover': {
                        background: (theme) => alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    Sign In
                  </Button>
                  <Button
                    variant="contained"
                    onClick={onOpenAuth}
                    sx={{
                      background: gradients.primary,
                      textTransform: 'none',
                      fontWeight: 700,
                      '&:hover': {
                        background: gradients.primary,
                        opacity: 0.9,
                      },
                    }}
                  >
                    Sign Up Free
                  </Button>
                </>
              )}
            </Box>

            {/* Mobile Menu Button */}
            <IconButton
              sx={{ ml: 'auto', display: { md: 'none' } }}
              onClick={() => setMobileOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: { width: 280, p: 2 },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: '1.5rem',
              background: gradients.primary,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              cursor: 'pointer',
            }}
          >
            addrs
          </Typography>
          <IconButton onClick={() => setMobileOpen(false)}>
            <Close />
          </IconButton>
        </Box>

        <List>
          {navItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton onClick={() => handleNavClick(item.path)}>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Box sx={{ mt: 'auto', pt: 2 }}>
          {isAuthenticated ? (
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                history.push('/dashboard');
                setMobileOpen(false);
              }}
              sx={{
                background: gradients.primary,
                textTransform: 'none',
                fontWeight: 700,
              }}
            >
              Dashboard
            </Button>
          ) : (
            <>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  onOpenAuth?.();
                  setMobileOpen(false);
                }}
                sx={{ mb: 1 }}
              >
                Sign In
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  onOpenAuth?.();
                  setMobileOpen(false);
                }}
                sx={{
                  background: gradients.primary,
                  textTransform: 'none',
                  fontWeight: 700,
                }}
              >
                Sign Up Free
              </Button>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};
